import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Wrench, Globe, ShieldCheck, Award, TrendingUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { products, certifications, regions } from '../data/mockData';

const Home = () => {
  const { language } = useLanguage();

  const heroContent = {
    en: {
      line1: 'Experts in Procurement',
      line2: 'and Export of',
      line3: 'Products and Services',
      tagline: 'Indian Excellence for Global Markets',
      description: 'At IVARME Trading Company, we empower businesses to procure high-quality industrial products and outsource engineering design with confidence.',
      products: 'Products',
      viewServices: 'Engineering Services'
    },
    vi: {
      line1: 'Chuyên gia mua sắm',
      line2: 'và Xuất khẩu',
      line3: 'Sản phẩm và Dịch vụ',
      tagline: 'Sự xuất sắc của Ấn Độ cho thị trường toàn cầu',
      description: 'Tại Công ty TNHH IVARME, chúng tôi trao quyền cho các doanh nghiệp mua sắm sản phẩm công nghiệp chất lượng cao và thuê ngoài thiết kế kỹ thuật với sự tự tin.',
      products: 'Sản phẩm',
      viewServices: 'Dịch vụ kỹ thuật'
    }
  };

  const t = heroContent[language];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-8 md:py-14 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80" 
            alt="Global Trade Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/92 to-[#264f78]/15"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              {t.line1}
              <br />
              {t.line2}
              <br />
              <span className="text-[#264f78]">{t.line3}</span>
            </h1>
            <div className="mb-6">
              <span className="inline-block bg-[#264f78]/10 text-black px-6 py-3 rounded-full text-lg md:text-xl font-bold">
                {t.tagline}
              </span>
            </div>
            <p className="text-xl md:text-2xl text-gray-700 mb-10 max-w-4xl mx-auto leading-relaxed font-medium">
              {t.description}
            </p>

            {/* What We Do Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-8 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                {/* Trade Operations */}
                <div>
                  <h3 className="text-lg font-bold text-[#264f78] mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    {language === 'en' ? 'Trade Operations' : 'Hoạt động thương mại'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {language === 'en' 
                      ? 'Our expertise spans the merchant export of:' 
                      : 'Chuyên môn của chúng tôi bao gồm xuất khẩu thương mại:'}
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'Industrial machinery & equipment' : 'Máy móc & thiết bị công nghiệp'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'MEP equipment & spares' : 'Thiết bị MEP & phụ tùng'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'Water pumps, pipe fittings & valves' : 'Máy bơm nước, phụ kiện ống & van'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'Swimming pool systems & accessories' : 'Hệ thống hồ bơi & phụ kiện'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'Water treatment chemicals' : 'Hóa chất xử lý nước'}</span>
                    </li>
                  </ul>
                </div>

                {/* Engineering Services */}
                <div>
                  <h3 className="text-lg font-bold text-[#264f78] mb-4 flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    {language === 'en' ? 'Engineering Services' : 'Dịch vụ kỹ thuật'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {language === 'en'
                      ? 'Our engineering division specializes in:'
                      : 'Bộ phận kỹ thuật chuyên về:'}
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'CAD production drawings' : 'Bản vẽ sản xuất CAD'}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-[#264f78] mt-1">•</span>
                      <span>{language === 'en' ? 'MEP design services' : 'Dịch vụ thiết kế MEP'}</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                <p className="text-base md:text-lg text-gray-800 font-semibold leading-relaxed">
                  {language === 'en'
                    ? 'Partner with IVARME for a seamless experience in design, procurement, and export'
                    : 'Hợp tác với IVARME để có trải nghiệm liền mạch trong thiết kế, mua sắm và xuất khẩu'}
                  <br />
                  {language === 'en'
                    ? '—where quality and reliability drive every project.'
                    : '—nơi chất lượng và độ tin cậy thúc đẩy mọi dự án.'}
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products">
                <Button className="bg-[#264f78] hover:bg-[#1a3a57] text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg">
                  {t.products}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button className="bg-[#264f78] hover:bg-[#1a3a57] text-white px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg">
                  {t.viewServices}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose IVARME */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Why Partner with IVARME?' : 'Tại sao hợp tác với IVARME?'}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {language === 'en'
                ? 'We combine technical excellence with global reach to deliver seamless procurement and export solutions'
                : 'Chúng tôi kết hợp sự xuất sắc về kỹ thuật với tầm với toàn cầu để cung cấp giải pháp mua sắm và xuất khẩu liền mạch'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#264f78]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-8 h-8 text-[#264f78]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'en' ? 'Quality Procurement' : 'Mua sắm chất lượng'}
              </h3>
              <p className="text-gray-600">
                {language === 'en'
                  ? 'High-quality Indian products sourced from trusted manufacturers with rigorous quality control'
                  : 'Sản phẩm Ấn Độ chất lượng cao từ các nhà sản xuất đáng tin cậy với kiểm soát chất lượng nghiêm ngặt'}
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#264f78]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-[#264f78]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'en' ? 'Engineering Excellence' : 'Xuất sắc kỹ thuật'}
              </h3>
              <p className="text-gray-600">
                {language === 'en'
                  ? 'International Standards compliant design services delivering precise technical solutions'
                  : 'Dịch vụ thiết kế tuân thủ tiêu chuẩn quốc tế cung cấp giải pháp kỹ thuật chính xác'}
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#264f78]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-[#264f78]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {language === 'en' ? 'Global Reach' : 'Tầm với toàn cầu'}
              </h3>
              <p className="text-gray-600">
                {language === 'en'
                  ? 'Strong presence across Vietnam, Middle East, and Africa with reliable logistics support'
                  : 'Sự hiện diện mạnh mẽ tại Việt Nam, Trung Đông và Châu Phi với hỗ trợ hậu cần đáng tin cậy'}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#264f78] to-[#1a3a57] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'en' ? 'Ready to Get Started?' : 'Sẵn sàng bắt đầu?'}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {language === 'en' ?
            'Request a quote for your product or engineering service requirements. Our team will respond within 24 hours.' :
            'Yêu cầu báo giá cho sản phẩm hoặc yêu cầu dịch vụ kỹ thuật của bạn. Nhóm của chúng tôi sẽ phản hồi trong vòng 24 giờ.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/request-quote">
              <Button className="bg-white text-[#264f78] hover:bg-gray-100 px-8 py-6 text-lg transition-all duration-300 hover:shadow-lg">
                {language === 'en' ? 'Request a Quote' : 'Yêu cầu báo giá'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#264f78] px-8 py-6 text-lg transition-all duration-300">
                {language === 'en' ? 'Contact Us' : 'Liên hệ'}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;